import { ArrowRightIcon } from 'assets/icons';
import { CustomSwitch, If } from 'components';
import ListDropDown from 'components/list-dropdown/ListDropDown';
import { TIP_TIMING_OPTIONS, TipTimingEnum } from 'containers/invoices/constants';
import { selectInvoiceDefaultsFromSetting } from 'containers/invoices/InvoicesSlice';
import { PartialPaymentOption } from 'containers/settings/constants';
import { useAppSelector } from 'hooks';
import { FC } from 'react';

export const PARTIAL_PAYMENT_DESCRIPTION =
  'Allow customers to make a payment less than the full amount due on the invoice.';
export const TIPS_DESCRIPTION = 'Allow customers to leave a tip';

interface OtherOptionsSectionProps {
  partialPaymentEnabled: boolean;
  onPartialPaymentToggle: () => void;
  tipEnabled: boolean;
  onTipToggle: () => void;
  onTipsLearnMoreClick: () => void;
  tipTiming: TipTimingEnum;
  onTipTimingChange: (selectedOption: { id: TipTimingEnum; label: string }) => void;
  isEdit?: boolean;
  showTipsSection: boolean;
}

const OtherOptionsSection: FC<OtherOptionsSectionProps> = ({
  partialPaymentEnabled,
  onPartialPaymentToggle,
  tipEnabled,
  onTipToggle,
  onTipsLearnMoreClick,
  tipTiming,
  onTipTimingChange,
  isEdit = false,
  showTipsSection
}) => {
  const defaultRemindersFromSettings = useAppSelector(selectInvoiceDefaultsFromSetting);

  const editPrefix = isEdit ? 'edit-invoice-' : 'create-invoice-';

  const partialPaymentToggleId = `${editPrefix}partial-payment-toggle-switch`;
  const tipToggleId = `${editPrefix}allow-tip-toggle-switch`;
  const learnMoreId = `${editPrefix}learn-more`;
  const tipTimingDropdownId = `${editPrefix}tip-timing-dropdown`;
  const isPartialPaymentDisabled = defaultRemindersFromSettings?.partialPayment === PartialPaymentOption.ALWAYS_ON;
  return (
    <div>
      <div className="mb-3 text-17px font-semibold text-heading">Other options</div>
      <div className="rounded border border-x-borderGray p-4">
        <div
          className={`relative flex flex-row items-center gap-3 ${
            isPartialPaymentDisabled ? 'pointer-events-none' : ''
          }`}>
          <If condition={isPartialPaymentDisabled}>
            <div className="slant-lines absolute left-0 z-50 h-full w-full rounded opacity-40" />
          </If>
          <CustomSwitch
            enabled={partialPaymentEnabled}
            toggleSwitch={onPartialPaymentToggle}
            disabled={isPartialPaymentDisabled}
            id={partialPaymentToggleId}
          />
          <div className="flex-1">
            <div className="text-17px font-semibold text-primaryText">Partial payments</div>
            <div className="text-sbase text-accentText">{PARTIAL_PAYMENT_DESCRIPTION}</div>
          </div>
        </div>

        {/* Tips Section */}
        {showTipsSection && (
          <div className="mt-3 border-t border-borderGray pt-3">
            <div className="flex flex-row items-center gap-3">
              <CustomSwitch enabled={tipEnabled} toggleSwitch={onTipToggle} id={tipToggleId} />
              <div className="flex-1">
                <div className="text-17px font-semibold text-primaryText">Tips</div>
                <div className="text-sbase text-accentText">{TIPS_DESCRIPTION}</div>
              </div>
              <div onClick={onTipsLearnMoreClick} className="ml-auto flex cursor-pointer flex-row items-center">
                <div id={learnMoreId} className="mr-2 text-sbase font-semibold text-secondary">
                  Learn more
                </div>
                <ArrowRightIcon className="shrink-0" />
              </div>
            </div>

            {/* Tips timing options - only show when tips are enabled */}
            <If condition={tipEnabled && partialPaymentEnabled}>
              <div className="mt-2 pl-12">
                <div className="w-full max-w-xs">
                  <ListDropDown
                    id={tipTimingDropdownId}
                    selectedItem={TIP_TIMING_OPTIONS.find(option => option.id === tipTiming) || TIP_TIMING_OPTIONS[0]}
                    items={TIP_TIMING_OPTIONS}
                    setSelectedItem={onTipTimingChange}
                    idKey="id"
                    nameKey="label"
                    buttonStyles="text-sbase bg-secondaryBtn px-3 py-2 h-[50px]"
                    placeholder="Select timing"
                    itemStyle="font-normal"
                    optionStyles="py-1.5"
                  />
                </div>
              </div>
            </If>
          </div>
        )}
      </div>
    </div>
  );
};

export default OtherOptionsSection;
